import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faCalendarStar, faImages, faNewspaper, faTags} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Modal} from 'antd';
import {useState} from 'react';

import {appRoute} from '../../../../app-route';
import {NavigationLink} from '../../../../layout/navigation-link/navigation-link';
import {Locale} from '../../../../provider/locale/locale';
import {LangKeyType} from '../../../../provider/locale/translation/type';
import {HelpLinkFeatureFlagEnum} from '../../../../service/feature-flags/feature-flag-const';
import {useFeatureFlag} from '../../../../service/feature-flags/use-feature-flag';
import {
    PublicationCreateFormModal,
    PublicationFormEnum,
} from '../publication-create-form-modal/publication-create-form-modal';

import * as styles from './posts-create-publication-button.scss';

const FORM_OPTION_LIST: Array<{form: PublicationFormEnum; icon: IconDefinition; labelLangKey: LangKeyType}> = [
    {form: PublicationFormEnum.Story, icon: faImages, labelLangKey: 'POSTS__CREATE_PUBLICATION__TYPE__STORY'},
    {form: PublicationFormEnum.Promotion, icon: faTags, labelLangKey: 'POSTS__CREATE_PUBLICATION__TYPE__PROMOTION'},
    {form: PublicationFormEnum.Event, icon: faCalendarStar, labelLangKey: 'POSTS__CREATE_PUBLICATION__TYPE__EVENT'},
];

type PropsType = {
    isLicenseExpired: boolean;
    onCreateSuccess: () => void;
};

export function PostsCreatePublicationButton({isLicenseExpired, onCreateSuccess}: PropsType): JSX.Element {
    // Stories, Promotions and Events live behind the publication-catalog rollout: while it is off the
    // calendar cannot show them, so offering to create one would hide the result from its author.
    const isPublicationCatalogEnabled = useFeatureFlag(HelpLinkFeatureFlagEnum.publicationCatalog);

    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
    const [openedForm, setOpenedForm] = useState<PublicationFormEnum | null>(null);

    // Only a Post is creatable, so the chooser would hold a single option — link straight to its page.
    if (!isPublicationCatalogEnabled) {
        return (
            <NavigationLink to={appRoute.postsManagementCreatePost.path}>
                <Button disabled={isLicenseExpired} type="primary">
                    <Locale stringKey="POSTS__CREATE_PUBLICATION__BUTTON" />
                </Button>
            </NavigationLink>
        );
    }

    return (
        <>
            <Button disabled={isLicenseExpired} onClick={() => setIsTypeModalOpen(true)} type="primary">
                <Locale stringKey="POSTS__CREATE_PUBLICATION__BUTTON" />
            </Button>

            <Modal
                centered
                destroyOnClose
                footer={null}
                onCancel={() => setIsTypeModalOpen(false)}
                open={isTypeModalOpen}
                title={<Locale stringKey="POSTS__CREATE_PUBLICATION__TITLE" />}
                width={480}
            >
                <div className={styles.CreatePublication_optionList}>
                    <NavigationLink to={appRoute.postsManagementCreatePost.path}>
                        <Button
                            block
                            className={styles.CreatePublication_option}
                            icon={<FontAwesomeIcon icon={faNewspaper} />}
                            size="large"
                        >
                            <Locale stringKey="POSTS__CREATE_PUBLICATION__TYPE__POST" />
                        </Button>
                    </NavigationLink>

                    {FORM_OPTION_LIST.map(({form, icon, labelLangKey}) => (
                        <Button
                            block
                            className={styles.CreatePublication_option}
                            icon={<FontAwesomeIcon icon={icon} />}
                            key={form}
                            onClick={() => {
                                setIsTypeModalOpen(false);
                                setOpenedForm(form);
                            }}
                            size="large"
                        >
                            <Locale stringKey={labelLangKey} />
                        </Button>
                    ))}
                </div>
            </Modal>

            <PublicationCreateFormModal
                form={openedForm}
                onClose={() => setOpenedForm(null)}
                onCreateSuccess={onCreateSuccess}
            />
        </>
    );
}
